// apiService.ts - Frontend API Service Layer
import axios, { AxiosInstance, AxiosError } from 'axios';
import env from './config/env';
import logger from './utils/logger';
import {
  ExtendedAxiosRequestConfig,
  QuizHistoryItem,
  QuizDetails,
  StudyProgressItem,
  BookmarkData,
  BookmarkResponse,
  LeaderboardResponse,
  PersonalAnalytics,
  GlobalAnalytics,
  UserAchievements,
  UserStats,
  FlagData,
  FlagResponse,
  FlagCheckResponse,
  CreateQuestionData,
  CreateQuestionResponse,
  ActivityLogsResponse,
  ActivityComparison,
  ActivityLog,
  EmailData,
  EmailResponse,
  CacheEntry,
  CacheStats,
  ApiResponse
} from './types/api.types';
import { Achievement, QuizResult, User } from './types';

// Re-export User type for backward compatibility
export type { User } from './types';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest extends LoginRequest {
  username: string;
  fullName?: string;
}

export interface QuizSubmission {
  standard: 'ivs' | 'rics';
  mode: 'practice' | 'exam';
  score: number;
  totalQuestions: number;
  percentage: number;
  timeSeconds: number;
  questions: QuestionSubmission[];
}

export interface QuestionSubmission {
  questionId?: string | null;
  questionText: string;
  questionType?: string;
  userAnswer: number;
  correctAnswer: number;
  isCorrect: boolean;
  difficulty?: string;
  category?: string | null;
  timeSpent?: number;
}

export interface QuestionResult {
  questionText: string;
  userAnswer: number;
  correctAnswer: number;
  isCorrect: boolean;
  difficulty?: string;
  category?: string;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string>;
}

// Shared quiz result type for public viewing
export interface SharedQuizResult {
  quiz: {
    id: string;
    standard: string;
    mode: string;
    score: number;
    totalQuestions: number;
    percentage: number;
    timeSeconds: number;
    completedAt: string;
    sharedAt: string;
    ogImageUrl?: string | null;
  };
  user: {
    name: string;
    avatar: string | null;
  };
  questionResults: Array<{
    question: string;
    questionType: string;
    category: string | null;
    difficulty: string | null;
    userAnswer: any;
    correctAnswer: any;
    options: any;
    explanation: string | null;
    isCorrect: boolean;
    bandScore: number | null;
    aiFeedback: string | null;
  }>;
}

// Types for Question-related data
interface RubricBand {
  definition: string;
  examples: string[];
}

interface QuestionRubric {
  band0?: RubricBand;
  band1?: RubricBand;
  band2?: RubricBand;
  band3?: RubricBand;
  band4?: RubricBand;
}

interface QuestionMetadata {
  source?: string;
  yearAsked?: number;
  tags?: string[];
  notes?: string;
}

interface RankingItem {
  text: string;
  correctOrder: number;
  id?: string | number;
}

interface ScoringConfig {
  id?: string;
  standard: string;
  category?: string;
  pointsPerCorrect: number;
  penaltyPerIncorrect?: number;
  timeBonus?: boolean;
  timeBonusSeconds?: number;
  timeBonusPoints?: number;
  minPoints?: number;
  maxPoints?: number;
  isActive?: boolean;
}

export interface QuizDistributionConfig {
  id: string;
  typeDistribution: Record<string, number>;
  difficultyDistribution: Record<string, number>;
  distributionMode: 'strict' | 'flexible';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  code: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    standards: number;
  };
  standards?: Partial<Standard>[];
}

export interface Standard {
  id: string;
  code: string;
  title: string;
  nickname: string;
  displayName: string;
  description: string | null;
  /**
   * Computed category slug aliased server-side from `categoryRef.code`.
   * The legacy `standards.category` varchar was dropped in ADR-012; this
   * field is now purely a convenience alias for keying into the UI
   * palette in `STANDARD_CATEGORIES`. Prefer `categoryRef` for the
   * resolved name/description; this may be null if a standard somehow
   * has no categoryRef (currently impossible in production).
   */
  category: string | null;
  categoryId?: string | null;
  categoryRef?: Category;
  coverImage: string | null;
  contentUrl: string | null;
  isActive: boolean;
  displayOrder?: number;
  allowedQuestionTypes?: string[] | null; // Array of question type IDs that can be generated for this standard
  createdAt: string;
  updatedAt: string;
}

// Sub-Standard Types
export interface SectionRef {
  level: 2 | 3;
  title: string;
  index?: number;
}

export interface ParsedSection {
  index: number;
  level: 2 | 3;
  title: string;
  parentIndex?: number;
  startLine: number;
  endLine: number;
}

export interface SectionTree {
  chapter: ParsedSection;
  subchapters: ParsedSection[];
}

export interface SubStandard {
  id: string;
  standardId: string;
  name: string;
  code: string;
  description: string | null;
  sectionRefs: SectionRef[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  standard?: {
    id: string;
    code: string;
    title: string;
    displayName: string;
  };
}

export interface StandardSectionsResponse {
  sections: ParsedSection[];
  tree: SectionTree[];
}

// Homepage Types
export interface HomepageRow {
  id: string;
  displayOrder: number;
  columns: number;
  gap: string | null;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
  items: HomepageItem[];
}

export interface HomepageItem {
  id: string;
  itemType: 'category' | 'standard' | 'custom_block' | 'spacer';
  categoryId: string | null;
  standardId: string | null;
  displayOrder: number;
  isVisible: boolean;
  // Grid positioning
  rowId: string | null;
  columnIndex: number;
  columnSpan: number;
  // Category-specific
  showCategoryHeader: boolean | null;
  // Visual customization
  customTitle: string | null;
  customDescription: string | null;
  customIcon: string | null;
  customColor: string | null;
  // Custom block fields
  customContent: string | null;
  customLinkUrl: string | null;
  customLinkText: string | null;
  // Study mode toggle
  showStudyMode: boolean | null;
  // Spacer fields
  spacerHeight: number | null;
  createdAt: string;
  updatedAt: string;
  category?: Category;
  standard?: Standard;
  row?: HomepageRow;
}

export interface HomepageConfig {
  rows: HomepageRow[];
  orphanItems: HomepageItem[];
}

// Reading Progress Types
export interface ReadingProgressAPI {
  id?: string;
  userId: string;
  sectionId: string;
  standard: string;
  firstReadAt: string; // ISO date string
  lastReadAt: string; // ISO date string
  totalTimeSpent: number;
  scrollDepth: number;
  isCompleted: boolean;
  deviceId?: string;
}

export interface ReadingProgressBatch {
  progress: Partial<ReadingProgressAPI>[];
}

export interface ReadingProgressResponse {
  success: boolean;
  data?: ReadingProgressAPI[];
  syncedCount?: number;
  message?: string;
}

// Review Item types for spaced repetition
export interface ReviewItem {
  id: string;
  userId: string;
  questionId: string;
  nextReviewDate: string;
  lastReviewed: string;
  reviewCount: number;
  easeFactor: number;
  interval: number;
  standardCode: string;
  questionType: string;
  difficulty: string | null;
  addedAt: string;
  lastAnsweredCorrectly: boolean;
}

export interface ReviewItemWithQuestion extends ReviewItem {
  question: {
    id: string;
    standard: string;
    sectionId: string | null;
    questionType: string;
    question: string;
    options: unknown;
    correctAnswer: unknown;
    explanation: string;
    reference: string;
    difficulty: string | null;
    metadata: unknown;
    hint: string | null;
    intent: string | null;
    contextTip: string | null;
    rubric: unknown;
    sourceExcerpt: string | null;
    sectionRefs: unknown;
  };
}

export interface ReviewStats {
  totalItemsInReview: number;
  dueForReview: number;
  reviewedToday: number;
  averageRetention: number;
  upcomingReviews: {
    today: number;
    thisWeek: number;
    thisMonth: number;
  };
}

export interface ReviewStandardInfo {
  code: string;
  title: string;
  totalCount: number;
  dueCount: number;
}

// Announcement types for pop-up modal system
export interface AnnouncementTargeting {
  roles?: ('student' | 'instructor' | 'admin')[];
  experienceLevels?: ('beginner' | 'intermediate' | 'expert')[];
  isFirstLogin?: boolean;
  isAuthenticated?: boolean;
  registeredAfter?: string;
  registeredBefore?: string;
}

export interface Announcement {
  id: string;
  title: string;
  body: string;
  imageUrl?: string | null;
  ctaText?: string | null;
  ctaLink?: string | null;
  ctaColor?: 'blue' | 'green' | 'orange' | 'purple';
  priority: number;
  startDate: string;
  endDate: string;
  dismissalType: 'never' | 'session' | 'daily' | 'weekly';
  targeting: AnnouncementTargeting;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  dismissalCount?: number;
}

export interface CreateAnnouncementData {
  title: string;
  body: string;
  imageUrl?: string | null;
  ctaText?: string | null;
  ctaLink?: string | null;
  ctaColor?: 'blue' | 'green' | 'orange' | 'purple';
  priority?: number;
  startDate: string;
  endDate: string;
  dismissalType?: 'never' | 'session' | 'daily' | 'weekly';
  targeting?: AnnouncementTargeting;
  isActive?: boolean;
}

export interface AnnouncementListResponse {
  announcements: Announcement[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: {
    total: number;
    activeNow: number;
    scheduled: number;
    expired: number;
  };
}

// API Service Class
class ApiService {
  public api: AxiosInstance;
  private refreshPromise: Promise<void> | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private csrfToken: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: env.API_URL,
      timeout: 30000, // Increased to 30 seconds for slower connections
      withCredentials: true, // Enable sending cookies with requests
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Start proactive token refresh
    this.startTokenRefreshTimer();

    // Request interceptor - add CSRF token to state-changing requests
    this.api.interceptors.request.use(
      async (config) => {
        // Add CSRF token for state-changing requests
        if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
          // Skip CSRF for auth endpoints
          const skipUrls = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/google', '/auth/magic-link'];
          const needsCsrf = !skipUrls.some(url => config.url?.includes(url));
          
          if (needsCsrf) {
            // Get CSRF token if we don't have one
            if (!this.csrfToken) {
              await this.fetchCsrfToken();
            }
            // Add CSRF token to headers
            if (this.csrfToken) {
              config.headers['X-CSRF-Token'] = this.csrfToken;
            }
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh and errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        // Handle network errors
        if (!error.response && error.code === 'ECONNABORTED') {
          logger.error('Request timeout - network may be slow');
          // Don't logout on timeout, just reject the promise
          return Promise.reject(new Error('Request timeout. Please check your connection and try again.'));
        }
        
        if (!error.response) {
          logger.error('Network error - unable to reach server');
          // Don't logout on network errors, the user might just be offline temporarily
          return Promise.reject(new Error('Network error. Please check your connection.'));
        }

        // Handle 403 - CSRF token invalid
        if (error.response?.status === 403) {
          const errorData = error.response.data as any;
          if (errorData?.error?.code === 'CSRF_ERROR' && !originalRequest._csrfRetry) {
            // Mark that we've tried to refresh CSRF token
            originalRequest._csrfRetry = true;
            
            // Refresh CSRF token and retry request
            logger.warn('CSRF token invalid, refreshing...');
            await this.refreshCsrfToken();
            
            // Retry the original request with new CSRF token
            if (this.csrfToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['X-CSRF-Token'] = this.csrfToken;
              return this.api(originalRequest);
            }
          }
        }

        // Handle 401 - Token expired or invalid
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          // Don't try to refresh for auth endpoints
          if (originalRequest.url?.includes('/auth/login') || 
              originalRequest.url?.includes('/auth/refresh')) {
            return Promise.reject(error);
          }

          // Prevent multiple refresh calls
          if (!this.refreshPromise) {
            this.refreshPromise = this.refreshAccessToken();
          }

          try {
            await this.refreshPromise;
            this.refreshPromise = null;
            return this.api(originalRequest);
          } catch (refreshError) {
            this.refreshPromise = null;
            
            // Check if auth is recent (within last 60 seconds) before logging out
            const authTimestamp = localStorage.getItem('authTimestamp');
            const isRecentAuth = authTimestamp && (Date.now() - parseInt(authTimestamp)) < 60000;
            
            if (isRecentAuth) {
              logger.warn('Token refresh failed but auth is recent, not logging out');
              return Promise.reject(refreshError);
            }
            
            logger.error('Token refresh failed, redirecting to login...');
            this.logout();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // CSRF token management
  private async fetchCsrfToken(): Promise<void> {
    try {
      const response = await this.api.get('/csrf-token');
      this.csrfToken = response.data.csrfToken;
      // Also check if it's in the response headers
      if (!this.csrfToken && response.headers['x-csrf-token']) {
        this.csrfToken = response.headers['x-csrf-token'];
      }
    } catch (error) {
      logger.error('Failed to fetch CSRF token:', error);
    }
  }
  
  // Public method to refresh CSRF token
  public async refreshCsrfToken(): Promise<void> {
    this.csrfToken = null;
    await this.fetchCsrfToken();
  }
  
  // No longer needed - cookies are handled automatically
  public refreshTokenReference(): void {
    // This method is kept for backward compatibility but does nothing
    // Cookies are handled automatically by the browser
  }

  private setTokens(tokens: AuthTokens): void {
    // Tokens are now set as httpOnly cookies by the backend
    // We don't store them in localStorage anymore
    // Just restart the refresh timer if needed
    if (tokens.expiresIn) {
      this.startTokenRefreshTimer(tokens.expiresIn);
    }
  }

  private clearTokens(): void {
    // Clear all auth-related data from localStorage
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authTimestamp');
    
    // Clear refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
  
  // Proactive token refresh - refreshes token before it expires
  private startTokenRefreshTimer(expiresIn?: number): void {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    
    if (!expiresIn) {
      return;
    }
    
    // Refresh 5 minutes before expiry (or immediately if less than 5 minutes left)
    const refreshIn = Math.max(0, (expiresIn * 1000) - (5 * 60 * 1000));
    
    if (refreshIn > 0) {
      logger.info(`Token refresh scheduled in ${Math.round(refreshIn / 1000 / 60)} minutes`);
      this.refreshTimer = setTimeout(async () => {
        try {
          logger.info('Proactively refreshing token...');
          await this.refreshAccessToken();
          logger.info('Token refreshed successfully');
        } catch (error) {
          logger.error('Proactive token refresh failed:', error);
        }
      }, refreshIn);
    } else {
      // Token expires soon, refresh immediately
      logger.warn('Token expires soon, refreshing immediately...');
      this.refreshAccessToken().catch((error) => logger.error('Token refresh error:', error));
    }
  }

  // Helper methods for AdminPageOptimized
  getBaseUrl(): string {
    return this.api.defaults.baseURL || 'http://localhost:3001/api';
  }

  getAuthHeaders(): Record<string, string> {
    // Authentication is now handled via httpOnly cookies
    return {
      'Content-Type': 'application/json'
    };
  }

  // Auth Methods
  async register(data: RegisterRequest): Promise<{ user: User }> {
    const response = await this.api.post('/auth/register', data);
    // Tokens are now set as httpOnly cookies by the backend
    localStorage.setItem('currentUser', JSON.stringify(response.data.user));
    return response.data;
  }

  async login(data: LoginRequest): Promise<{ user: User }> {
    logger.debug('Login API call to:', this.api.defaults.baseURL + '/auth/login');
    const response = await this.api.post('/auth/login', data);
    // Tokens are now set as httpOnly cookies by the backend
    localStorage.setItem('currentUser', JSON.stringify(response.data.user));
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      this.clearTokens();
    }
  }

  async requestMagicLink(email: string, inviteCode?: string): Promise<{ message: string; success: boolean }> {
    const response = await this.api.post('/auth/magic-link', { email, inviteCode });
    return response.data;
  }

  // Invite code methods
  async getInviteStatus(): Promise<{ inviteOnlyMode: boolean }> {
    const response = await this.api.get('/invite/status');
    return response.data;
  }

  async validateInviteCode(code: string, email?: string): Promise<{ valid: boolean; emailLocked: boolean; lockedEmail?: string }> {
    const response = await this.api.post('/invite/validate', { code, email });
    return response.data;
  }

  // Admin invite code methods
  async getInviteStats(): Promise<{
    totalCodes: number;
    activeCodes: number;
    usedCodes: number;
    totalUsages: number;
    inviteOnlyMode: boolean;
  }> {
    const response = await this.api.get('/invite/stats');
    return response.data;
  }

  async listInviteCodes(params?: {
    page?: number;
    limit?: number;
    includeInactive?: boolean;
  }): Promise<{
    codes: Array<{
      id: string;
      code: string;
      email: string | null;
      maxUses: number;
      usedCount: number;
      expiresAt: string | null;
      createdAt: string;
      usedAt: string | null;
      isActive: boolean;
      createdBy: { id: string; email: string; fullName: string | null };
      usedBy: Array<{ id: string; email: string; fullName: string | null; createdAt: string }>;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const response = await this.api.get('/invite/list', { params });
    return response.data;
  }

  async generateInviteCode(options?: {
    email?: string;
    maxUses?: number;
    expiresInDays?: number;
    count?: number;
  }): Promise<{
    codes: Array<{
      id: string;
      code: string;
      email: string | null;
      maxUses: number;
      expiresAt: string | null;
    }>;
  }> {
    const response = await this.api.post('/invite/generate', options || {});
    return response.data;
  }

  async revokeInviteCode(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/invite/${id}`);
    return response.data;
  }

  async reactivateInviteCode(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.put(`/invite/${id}/reactivate`);
    return response.data;
  }

  async setInviteOnlyMode(enabled: boolean): Promise<{ success: boolean; inviteOnlyMode: boolean }> {
    const response = await this.api.post('/invite/mode', { enabled });
    return response.data;
  }

  async verifyMagicLink(token: string, email: string): Promise<{ user: User; tokens: AuthTokens }> {
    const response = await this.api.post('/auth/verify-magic-link', { token, email });
    this.setTokens(response.data.tokens);
    localStorage.setItem('currentUser', JSON.stringify(response.data.user));
    return response.data;
  }

  async refreshAccessToken(): Promise<void> {
    // Cookies are sent automatically with withCredentials: true
    // No need to manually send refresh token
    try {
      const response = await this.api.post('/auth/refresh', {});
      // Tokens are now set as cookies by the backend
      // Just update the timer if needed
      if (response.data.expiresIn) {
        this.startTokenRefreshTimer(response.data.expiresIn);
      }
    } catch (error) {
      // Refresh is now handled server-side with httpOnly cookies
      // The server will automatically use the refresh token from the cookie
      const response = await this.api.post('/auth/refresh');
      // Cookies are automatically set by the server
      return response.data;
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get('/auth/me');
    return response.data.user;
  }

  // Quiz Methods
  async submitQuiz(data: QuizSubmission): Promise<{
    quizId: string;
    pointsEarned: number;
    points: number; // Backend returns 'points', keeping both for compatibility
    newAchievements: Achievement[]; // Backend returns full achievement objects
    percentile: number;
    globalStats: GlobalAnalytics;
  }> {
    const response = await this.api.post('/quizzes/submit', data);
    return response.data;
  }

  async getQuizHistory(params?: {
    standard?: string;
    limit?: number;
    offset?: number;
  }): Promise<QuizHistoryItem[]> {
    const response = await this.api.get('/quizzes/history', { params });
    return response.data.quizzes;
  }

  async getQuizDetails(quizId: string): Promise<QuizDetails> {
    const response = await this.api.get(`/quizzes/${quizId}`);
    return response.data;
  }

  async getQuizQuestions(quizId: string): Promise<any[]> {
    const response = await this.api.get(`/quizzes/${quizId}/questions`);
    return response.data;
  }

  // Quiz Sharing Methods
  async enableQuizSharing(quizId: string): Promise<{ shareToken: string; shareUrl: string; ogImageUrl?: string | null }> {
    const response = await this.api.post(`/quizzes/results/${quizId}/share`);
    return response.data;
  }

  async disableQuizSharing(quizId: string): Promise<{ success: boolean }> {
    await this.api.delete(`/quizzes/results/${quizId}/share`);
    return { success: true };
  }

  async getSharedQuizResult(shareToken: string): Promise<any> {
    const response = await this.api.get(`/quizzes/public/${shareToken}`);
    return response.data;
  }

  // Get question count by standard
  async getQuestionCount(standard: string, sectionId?: string): Promise<{ standard: string; sectionId: string | null; count: number }> {
    const params: any = { standard };
    if (sectionId) {
      params.sectionId = sectionId;
    }
    const response = await this.api.get('/questions/count', { params });
    return response.data;
  }

  // Get question count by difficulty level
  async getQuestionCountByDifficulty(standard: string, sectionId?: string, subStandardIds?: string, sectionTitles?: string): Promise<{ easy: number; medium: number; hard: number; total: number }> {
    const params: any = { standard };
    if (sectionId) {
      params.sectionId = sectionId;
    }
    if (subStandardIds) {
      params.subStandardIds = subStandardIds;
    }
    if (sectionTitles) {
      params.sectionTitles = sectionTitles;
    }
    const response = await this.api.get('/questions/count-by-difficulty', { params });
    return response.data;
  }

  // Get question counts grouped by section for a standard
  async getQuestionCountBySection(standard: string): Promise<{
    standard: string;
    counts: Record<string, number>;
    total: number;
  }> {
    const response = await this.api.get('/questions/count-by-section', {
      params: { standard }
    });
    return response.data;
  }

  // Check warmup availability (all 7 question types must exist for the standard)
  async checkWarmupAvailability(standard: string): Promise<{ available: boolean }> {
    const response = await this.api.get('/quizzes/warmup-availability', {
      params: { standard }
    });
    return response.data;
  }

  // Study Mode Methods
  async markSectionAsRead(sectionId: string, timeSpent: number): Promise<void> {
    await this.api.post('/study/progress', { sectionId, timeSpent });
  }

  async getStudyProgress(standard?: string): Promise<StudyProgressItem[]> {
    const response = await this.api.get('/study/progress', {
      params: { standard }
    });
    return response.data.progress;
  }

  async addBookmark(data: {
    sectionId: string;
    sectionTitle: string;
    standard: string;
    note?: string;
  }): Promise<BookmarkResponse> {
    const response = await this.api.post('/study/bookmarks', data);
    return response.data.bookmark;
  }

  async getBookmarks(): Promise<BookmarkResponse[]> {
    const response = await this.api.get('/study/bookmarks');
    return response.data.bookmarks;
  }

  async deleteBookmark(bookmarkId: string): Promise<void> {
    await this.api.delete(`/study/bookmarks/${bookmarkId}`);
  }

  // Leaderboard Methods
  async getLeaderboard(params?: {
    period?: 'day' | 'week' | 'month' | 'all';
    standard?: string;
    limit?: number;
  }): Promise<LeaderboardResponse> {
    const response = await this.api.get('/leaderboard', { params });
    return response.data;
  }

  // Analytics Methods
  async getPersonalAnalytics(): Promise<PersonalAnalytics> {
    const response = await this.api.get('/analytics/personal');
    return response.data;
  }

  async getGlobalAnalytics(): Promise<GlobalAnalytics> {
    const response = await this.api.get('/analytics/global');
    return response.data;
  }

  // Achievements Methods
  async getUserAchievements(): Promise<UserAchievements> {
    const response = await this.api.get('/quizzes/achievements');
    return response.data;
  }

  // User Profile Methods
  async updateProfile(userId: string, data: Partial<User>): Promise<User> {
    const response = await this.api.put(`/auth/profile`, data);
    return response.data.user;
  }

  async uploadAvatar(userId: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await this.api.post(`/auth/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.avatarUrl;
  }

  async getUserStats(userId: string): Promise<UserStats> {
    const response = await this.api.get(`/users/${userId}/stats`);
    return response.data.stats;
  }

  // Question Flag methods
  async createFlag(data: {
    questionId: string;
    standard: string;
    flagType: 'incorrect' | 'unclear' | 'typo' | 'wrong_category' | 'other';
    comment?: string;
    questionText: string;
    correctAnswer: string;
    userAnswer?: string;
  }): Promise<FlagResponse> {
    const response = await this.api.post('/flags', data);
    return response.data;
  }

  // Alias for backward compatibility
  async flagQuestion(data: {
    questionId: string;
    standard: string;
    flagType: 'incorrect' | 'unclear' | 'typo' | 'wrong_category' | 'other';
    comment?: string;
    questionText: string;
    correctAnswer: string;
    userAnswer?: string;
  }): Promise<FlagResponse> {
    return this.createFlag(data);
  }

  async checkQuestionFlag(questionId: string): Promise<FlagCheckResponse> {
    const response = await this.api.get(`/flags/question/${questionId}`);
    return response.data;
  }

  async getFlags(params?: { status?: string; standard?: string }): Promise<FlagResponse[]> {
    const response = await this.api.get('/flags', { params });
    return response.data;
  }

  async updateFlag(id: string, data: { status: string; adminNotes?: string }): Promise<FlagResponse> {
    const response = await this.api.patch(`/flags/${id}`, data);
    return response.data;
  }

  // Reading Progress endpoints
  async syncReadingProgress(batch: ReadingProgressBatch): Promise<ReadingProgressResponse> {
    const response = await this.api.post('/study/progress/batch', batch);
    return response.data;
  }

  async getReadingProgress(userId?: string): Promise<ReadingProgressAPI[]> {
    const response = await this.api.get('/study/progress', {
      params: userId ? { userId } : {}
    });
    return response.data;
  }

  async markSectionComplete(sectionId: string): Promise<ReadingProgressResponse> {
    const response = await this.api.put(`/study/progress/${sectionId}/complete`);
    return response.data;
  }

  async getReadingProgressBySection(sectionId: string): Promise<ReadingProgressAPI | null> {
    const response = await this.api.get(`/study/progress/section/${sectionId}`);
    return response.data;
  }

  // Delete user's learning history
  async deleteUserHistory(): Promise<{ message: string }> {
    const response = await this.api.delete('/auth/history');
    return response.data;
  }

  // Standards Methods
  async getStandards(includeInactive = false): Promise<Standard[]> {
    const response = await this.api.get('/standards', {
      params: { includeInactive }
    });
    return response.data;
  }

  async getStandardByCode(code: string): Promise<Standard> {
    const response = await this.api.get(`/standards/${code}`);
    return response.data;
  }

  async createStandard(data: Partial<Standard>): Promise<Standard> {
    const response = await this.api.post('/standards', data);
    return response.data;
  }

  async updateStandard(id: string, data: Partial<Standard>): Promise<Standard> {
    const response = await this.api.put(`/standards/${id}`, data);
    return response.data.standard;
  }

  async deleteStandard(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/standards/${id}`);
    return response.data;
  }

  // Sub-Standard Methods
  async getStandardSections(standardId: string): Promise<StandardSectionsResponse> {
    const response = await this.api.get(`/standards/${standardId}/sections`);
    return response.data;
  }

  async getSubStandards(standardId: string, includeInactive = false): Promise<SubStandard[]> {
    const response = await this.api.get(`/standards/${standardId}/substandards`, {
      params: { includeInactive }
    });
    return response.data;
  }

  async getSubStandard(id: string): Promise<SubStandard> {
    const response = await this.api.get(`/substandards/${id}`);
    return response.data;
  }

  async createSubStandard(standardId: string, data: {
    name: string;
    code: string;
    description?: string;
    sectionRefs: SectionRef[];
    isActive?: boolean;
  }): Promise<SubStandard> {
    const response = await this.api.post(`/standards/${standardId}/substandards`, data);
    return response.data;
  }

  async updateSubStandard(id: string, data: Partial<{
    name: string;
    code: string;
    description: string;
    sectionRefs: SectionRef[];
    isActive: boolean;
  }>): Promise<SubStandard> {
    const response = await this.api.put(`/substandards/${id}`, data);
    return response.data;
  }

  async deleteSubStandard(id: string): Promise<void> {
    await this.api.delete(`/substandards/${id}`);
  }

  async toggleSubStandard(id: string): Promise<SubStandard> {
    const response = await this.api.post(`/substandards/${id}/toggle`);
    return response.data;
  }

  // Category Methods
  async getCategories(includeInactive = false): Promise<Category[]> {
    const response = await this.api.get('/categories', {
      params: { includeInactive }
    });
    return response.data;
  }

  async getCategory(id: string): Promise<Category> {
    const response = await this.api.get(`/categories/${id}`);
    return response.data;
  }

  async createCategory(formData: FormData): Promise<Category> {
    const response = await this.api.post('/categories', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async updateCategory(id: string, formData: FormData): Promise<Category> {
    const response = await this.api.put(`/categories/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async deleteCategory(id: string): Promise<void> {
    await this.api.delete(`/categories/${id}`);
  }

  async reorderCategories(orders: { id: string; displayOrder: number }[]): Promise<void> {
    await this.api.put('/categories/reorder', { orders });
  }

  // Homepage Methods
  async getHomepageConfig(): Promise<HomepageConfig> {
    const response = await this.api.get('/homepage/config');
    return response.data;
  }

  // Row Methods
  async getAllHomepageRows(): Promise<HomepageRow[]> {
    const response = await this.api.get('/homepage/rows');
    return response.data;
  }

  async createHomepageRow(data?: { columns?: number; gap?: string }): Promise<HomepageRow> {
    const response = await this.api.post('/homepage/rows', data || {});
    return response.data;
  }

  async updateHomepageRow(id: string, data: Partial<HomepageRow>): Promise<HomepageRow> {
    const response = await this.api.put(`/homepage/rows/${id}`, data);
    return response.data;
  }

  async deleteHomepageRow(id: string): Promise<void> {
    await this.api.delete(`/homepage/rows/${id}`);
  }

  async reorderHomepageRows(rows: { id: string; displayOrder: number }[]): Promise<void> {
    await this.api.put('/homepage/rows/reorder', { rows });
  }

  // Item Methods
  async getAllHomepageItems(): Promise<HomepageItem[]> {
    const response = await this.api.get('/homepage/items');
    return response.data;
  }

  async createHomepageItem(data: Partial<HomepageItem>): Promise<HomepageItem> {
    const response = await this.api.post('/homepage/items', data);
    return response.data;
  }

  async updateHomepageItem(id: string, data: Partial<HomepageItem>): Promise<HomepageItem> {
    const response = await this.api.put(`/homepage/items/${id}`, data);
    return response.data;
  }

  async moveHomepageItem(id: string, rowId: string, columnIndex: number, columnSpan?: number): Promise<HomepageItem> {
    const response = await this.api.put(`/homepage/items/${id}/move`, { rowId, columnIndex, columnSpan });
    return response.data;
  }

  async deleteHomepageItem(id: string): Promise<void> {
    await this.api.delete(`/homepage/items/${id}`);
  }

  async reorderHomepageItems(items: { id: string; displayOrder: number }[]): Promise<void> {
    await this.api.put('/homepage/reorder', { items });
  }

  // Question Methods
  async createQuestion(data: {
    standardId: string;
    question?: string;
    text?: string;
    options?: string[] | any;
    correctAnswer?: number | boolean | number[][] | string[] | any;
    correctMatches?: number[][];
    explanation: string;
    difficulty: 'easy' | 'medium' | 'hard';
    topic: string;
    type: 'multiple-choice' | 'true-false' | 'matching' | 'fill-blanks' | 'ranking' | 'calculation' | 'short-answer';
    instruction?: string;
    leftColumn?: string[];
    rightColumn?: string[];
    correctPairs?: number[][];
    blanks?: string[];
    questionWithBlanks?: string;
    correctOrder?: number[];
    answer?: number;
    tolerance?: number;
    unit?: string;
    formula?: string;
    variables?: Record<string, number> | any[];
    calculationSteps?: string[];  // Step-by-step solution for calculation questions
    items?: Array<{ id: number; text: string; correctOrder: number }>;
    intent?: string;
    contextTip?: string;
    rubric?: QuestionRubric;
    hint?: string;
    showFormula?: boolean;
    distractorRationale?: Record<string, string>;
    misconception?: string;
    metadata?: QuestionMetadata;
    expectedAnswer?: string;
    value?: number;
    sourceExcerpt?: string;  // AI-generated excerpt from source document for assessment context
  } & Record<string, unknown>): Promise<CreateQuestionResponse> {
    try {
      // Map to backend expected format
      const payload: Record<string, unknown> = {
        type: data.type,
        standard: data.standardId,
        difficulty: data.difficulty,
        category: data.topic,
        section: data.topic,
        explanation: data.explanation,
        hint: data.hint || null,
        generatedByModel: (data as any).generatedByModel || null,
        generationMetadata: (data as any).generationMetadata || null,
        sectionRefs: (data as any).sectionRefs || null  // Section references for sub-standard filtering
      };
      // Add type-specific fields
      if (data.type === 'matching') {
        // Matching questions - extract from options and correctAnswer if present
        if (data.options && typeof data.options === 'object') {
          payload.leftColumn = data.options.leftColumn || data.leftColumn;
          payload.rightColumn = data.options.rightColumn || data.rightColumn;
        } else {
          payload.leftColumn = data.leftColumn;
          payload.rightColumn = data.rightColumn;
        }
        
        if (data.correctAnswer && typeof data.correctAnswer === 'object') {
          payload.correctPairs = data.correctAnswer.correctMatches || data.correctPairs;
        } else {
          payload.correctPairs = data.correctPairs || data.correctMatches;
        }
        
        payload.instruction = data.instruction || data.text || data.question;
      } else if (data.type === 'fill-blanks') {
        // Fill-blanks questions
        payload.text = data.question || data.text;
        payload.blanks = data.blanks;
        payload.questionWithBlanks = data.questionWithBlanks;
        payload.correctAnswer = data.blanks; // For fill-blanks, correctAnswer is the array of blanks
        payload.hint = data.hint || null;
      } else if (data.type === 'ranking') {
        // Ranking questions
        payload.text = data.question;
        // If items are provided, extract options from them
        if (data.items && data.items.length > 0) {
          const items = data.items as RankingItem[];
          payload.options = items.map((item: RankingItem) => item.text);
          payload.correctOrder = items.map((item: RankingItem) => item.correctOrder);
          payload.items = items;
          payload.correctAnswer = items.map((item: RankingItem) => item.correctOrder);
        } else if (data.options) {
          // Fallback to options if items not provided
          payload.options = data.options;
          payload.correctOrder = data.correctOrder;
          payload.correctAnswer = data.correctOrder;
        } else {
          // Default empty array
          payload.options = [];
          payload.correctOrder = [];
          payload.correctAnswer = [];
        }
        payload.hint = data.hint || null;
      } else if (data.type === 'calculation') {
        // Calculation questions
        payload.text = data.question;
        // Handle both answer and correctAnswer fields, extracting value if it's an object
        let calcAnswer = data.answer || data.correctAnswer;
        if (typeof calcAnswer === 'object' && calcAnswer?.value !== undefined) {
          calcAnswer = calcAnswer.value;
        }
        payload.answer = calcAnswer;
        payload.tolerance = data.tolerance || 0.01;
        payload.unit = data.unit || '';
        payload.formula = data.formula;
        payload.variables = data.variables;
        payload.calculationSteps = data.calculationSteps;  // Step-by-step solution
        payload.correctAnswer = { value: calcAnswer };
        payload.showFormula = data.showFormula !== undefined ? data.showFormula : true;
        payload.hint = data.hint || null;
      } else if (data.type === 'short-answer') {
        // Short answer questions
        payload.text = data.question;
        payload.intent = data.intent;
        payload.contextTip = data.contextTip;
        payload.rubric = data.rubric;
        payload.sourceExcerpt = data.sourceExcerpt;  // AI-generated context for assessment
        payload.correctAnswer = data.correctAnswer || '';
      } else {
        // Multiple choice and true/false use 'text' field
        payload.text = data.question;
        payload.options = data.options;
        payload.correctAnswer = data.correctAnswer;
        
        // Include distractorRationale for multiple-choice questions
        if (data.type === 'multiple-choice' && data.distractorRationale) {
          payload.distractorRationale = data.distractorRationale;
        }
        
        // Include misconception for true-false questions
        if (data.type === 'true-false' && data.misconception) {
          payload.misconception = data.misconception;
        }
      }
      
      const response = await this.api.post('/admin/questions', payload);
      return response.data;
    } catch (error) {
      console.error('Failed to create question:', error);
      throw error;
    }
  }

  // Upload image file
  async uploadImage(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }


  // Upload content file (Markdown)
  async uploadContent(file: File, standardCode: string): Promise<{ url: string; filename: string; content: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('standardCode', standardCode);
    
    const response = await this.api.post('/upload/content', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  // Generic HTTP methods
  async get<T>(url: string, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.api.get(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.api.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.api.put(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.api.patch(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.api.delete(url, config);
    return response.data;
  }

  // ====== AI Management ======
  async updateMasterPrompt(prompt: string): Promise<void> {
    const response = await this.api.put('/ai/master-prompt', { prompt });
    return response.data;
  }

  async getMasterPrompt(): Promise<{ prompt: string }> {
    const response = await this.api.get('/ai/master-prompt');
    return response.data;
  }

  // ====== AI Usage History ======
  async getAIHistory(params?: {
    startDate?: string;
    endDate?: string;
    model?: string;
    standardId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    logs: ActivityLog[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const response = await this.api.get('/ai/history', { params });
    return response.data;
  }

  async getAIUsageStats(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<{
    summary: {
      totalGenerations: number;
      totalCost: number;
      totalTokens: number;
      successRate: number;
      averageCostPerGeneration: number;
    };
    modelUsage: Array<{
      model: string;
      provider: string;
      count: number;
      totalCost: number;
      totalTokens: number;
    }>;
    dailyUsage: Array<{
      date: string;
      generations: number;
      cost: number;
      tokens: number;
    }>;
  }> {
    const response = await this.api.get('/ai/history/stats', { params });
    return response.data;
  }

  async getAIErrors(): Promise<{
    errors: Array<{
      id: string;
      model: string;
      errorMessage: string;
      createdAt: string;
      user: {
        username: string;
        email: string;
      };
      standard?: {
        displayName: string;
      };
    }>;
  }> {
    const response = await this.api.get('/ai/history/errors');
    return response.data;
  }

  // OpenRouter Activity
  async getOpenRouterActivity(date?: string): Promise<{
    activity: Array<{
      date: string;
      model: string;
      provider_name: string;
      usage: number;
      requests: number;
      prompt_tokens: number;
      completion_tokens: number;
      reasoning_tokens: number;
    }>;
    apiKeys: Array<{
      hash: string;
      name: string;
      label: string;
      usage: number;
    }>;
    total: number;
  }> {
    const params = date ? { date } : {};
    const response = await this.api.get('/ai/activity/openrouter', { params });
    return response.data;
  }

  async syncOpenRouterActivity(date?: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await this.api.post('/ai/activity/sync', { date });
    return response.data;
  }

  // OpenRouter Credits
  async getOpenRouterCredits(): Promise<{
    total_credits: number;
    total_usage: number;
  }> {
    const response = await this.api.get('/ai/activity/credits');
    return response.data;
  }

  // Spending Summary by Time Period
  async getSpendingSummary(): Promise<{
    last24h: number;
    last7Days: number;
    last30Days: number;
    last90Days: number;
    last365Days: number;
  }> {
    const response = await this.api.get('/ai/spending-summary');
    return response.data;
  }

  async compareActivityData(date?: string): Promise<{
    local: ActivityLog[];
    openrouter: ActivityLog[];
    discrepancies: Array<{
      date: string;
      model: string;
      localCost: number;
      openrouterCost: number;
      difference: number;
      percentDifference: string;
      localRequests: number;
      openrouterRequests: number;
    }>;
  }> {
    const params = date ? { date } : {};
    const response = await this.api.get('/ai/activity/compare', { params });
    return response.data;
  }

  async getValuLearnSummary(): Promise<{
    totalCost: number;
    totalRequests: number;
    totalTokens: number;
    modelBreakdown: Array<{
      model: string;
      provider: string;
      cost: number;
      requests: number;
      tokens: number;
    }>;
  }> {
    const response = await this.api.get('/ai/activity/valulearn-summary');
    return response.data;
  }

  // Get AI generation logs
  async getAIGenerationLogs(params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    userId?: string;
    model?: string;
    success?: boolean;
    includeContent?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.model) queryParams.append('model', params.model);
    if (params?.success !== undefined) queryParams.append('success', params.success.toString());
    if (params?.includeContent !== undefined) queryParams.append('includeContent', params.includeContent.toString());
    
    const response = await this.api.get(`/ai/generation-logs?${queryParams.toString()}`);
    return response.data;
  }

  // Get single AI generation log with full content
  async getAIGenerationLog(logId: string) {
    const response = await this.api.get(`/ai/generation-logs/${logId}`);
    return response.data;
  }

  // Error handling helper
  getErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const apiError = error.response?.data as ApiError;
      const status = error.response?.status;
      
      // Handle specific status codes with user-friendly messages
      if (status === 409) {
        // Check if the error details provide more specific information
        if (apiError?.details) {
          if (apiError.details.email) {
            return 'This email address is already registered. Please use a different email or sign in.';
          }
          if (apiError.details.username) {
            return 'This username is already taken. Please choose a different username.';
          }
        }
        return apiError?.message || 'This email or username is already in use. Please try different credentials.';
      }
      
      if (status === 400) {
        return apiError?.message || 'Invalid information provided. Please check your details and try again.';
      }
      
      if (status === 401) {
        return 'Invalid email or password. Please try again.';
      }
      
      if (status === 404) {
        return 'Account not found. Please check your credentials or create a new account.';
      }
      
      if (status === 500) {
        return 'Server error. Please try again later or contact support if the problem persists.';
      }
      
      // If we have a specific message from the API, use it
      if (apiError?.message) {
        return apiError.message;
      }
      
      // Default Axios error message
      return error.message || 'An unexpected error occurred';
    }
    return 'An unexpected error occurred';
  }

  // =====================
  // Scoring Configuration Methods
  // =====================

  async getScoringConfigs() {
    const response = await this.api.get('/scoring/configs');
    return response.data;
  }

  async getScoringConfig(id: string) {
    const response = await this.api.get(`/scoring/config/${id}`);
    return response.data;
  }

  async getActiveScoringConfig() {
    const response = await this.api.get('/scoring/config/active');
    return response.data;
  }

  async getScoringPresets() {
    const response = await this.api.get('/scoring/config/presets');
    return response.data;
  }

  async createScoringConfig(data: ScoringConfig) {
    const response = await this.api.post('/scoring/config', data);
    return response.data;
  }

  async createScoringConfigFromPreset(presetName: string, customName: string) {
    const response = await this.api.post('/scoring/config/from-preset', {
      presetName,
      customName
    });
    return response.data;
  }

  async updateScoringConfig(id: string, data: Partial<ScoringConfig>) {
    const response = await this.api.put(`/scoring/config/${id}`, data);
    return response.data;
  }

  async activateScoringConfig(id: string) {
    const response = await this.api.post(`/scoring/config/${id}/activate`);
    return response.data;
  }

  async setDefaultScoringConfig(id: string) {
    const response = await this.api.post(`/scoring/config/${id}/set-default`);
    return response.data;
  }

  async deleteScoringConfig(id: string) {
    const response = await this.api.delete(`/scoring/config/${id}`);
    return response.data;
  }

  async calculateTestScore(params: {
    questionType: string;
    difficulty: string;
    isCorrect: boolean;
    timeSpent: number;
    streakCount: number;
    mode: string;
    hintUsed: boolean;
  }) {
    const response = await this.api.post('/scoring/calculate', params);
    return response.data;
  }

  // Standard Images
  async getStandardImages(standardId: string) {
    const response = await this.api.get(`/standards/${standardId}/images`);
    return response.data;
  }

  async detectStandardImages(standardId: string) {
    const response = await this.api.post(`/standards/${standardId}/images/detect`);
    return response.data;
  }

  async uploadStandardImage(standardId: string, imageName: string, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await this.api.post(
      `/standards/${standardId}/images/${imageName}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  }

  async deleteStandardImage(standardId: string, imageName: string) {
    const response = await this.api.delete(`/standards/${standardId}/images/${imageName}`);
    return response.data;
  }

  // ============= STUDY ENHANCEMENT METHODS =============

  // Study Notes
  async getStudyNotes(params?: { sectionId?: string; standardId?: string }) {
    const response = await this.api.get('/study-enhancement/notes', { params });
    return response.data;
  }

  async createStudyNote(data: {
    sectionId: string;
    standardId: string;
    content: string;
    category?: string;
    color?: string;
    position?: number;
    isPrivate?: boolean;
  }) {
    const response = await this.api.post('/study-enhancement/notes', data);
    return response.data;
  }

  async updateStudyNote(id: string, data: Partial<{
    content: string;
    category: string;
    color: string;
    position: number;
    isPrivate: boolean;
  }>) {
    const response = await this.api.put(`/study-enhancement/notes/${id}`, data);
    return response.data;
  }

  async deleteStudyNote(id: string) {
    const response = await this.api.delete(`/study-enhancement/notes/${id}`);
    return response.data;
  }


  // Study Enhancement Export
  async exportStudyNotesMarkdown(params?: { standardId?: string; sectionId?: string }) {
    const response = await this.api.get('/study-enhancement/export/notes/markdown', {
      params,
      responseType: 'text'
    });
    return response.data;
  }

  // Study Enhancement Statistics
  async getStudyEnhancementStats() {
    const response = await this.api.get('/study-enhancement/stats');
    return response.data;
  }

  // ============= AI EVALUATION METHODS =============

  /**
   * Evaluate a single short answer question
   */
  async evaluateShortAnswer(data: {
    model?: string;
    question: string;
    intent: string;
    rubric: unknown;
    learnerAnswer: string;
    sourceContent?: string;
    questionId?: string;
    generatedByModel?: string;
    assessmentPromptId?: string;
    mode?: 'practice' | 'exam';
  }) {
    const response = await this.api.post('/ai/evaluate-short-answer', data);
    return response.data;
  }

  /**
   * Batch evaluate multiple short answer questions (for exam mode)
   */
  async evaluateShortAnswerBatch(evaluations: Array<{
    model?: string;
    question: string;
    intent: string;
    rubric: unknown;
    learnerAnswer: string;
    sourceContent?: string;
    questionId?: string;
    generatedByModel?: string;
    assessmentPromptId?: string;
  }>) {
    // Make parallel requests for all evaluations
    const promises = evaluations.map(data =>
      this.evaluateShortAnswer(data)
    );
    return Promise.all(promises);
  }

  // ============= GENERATION REPORT METHODS =============

  /**
   * Get generation reports for a question
   * Returns all versions with statistics
   */
  async getQuestionGenerationReports(questionId: string) {
    const response = await this.api.get(`/questions/${questionId}/generation-reports`);
    return response.data;
  }

  // ============= AI DESCRIPTION GENERATION =============

  /**
   * Generate a description for a standard using AI
   * @param content - The markdown content of the standard
   * @param standardId - Optional ID of the standard (for logging)
   * @param standardName - Optional name of the standard (for logging)
   * @returns Generated description (300-700 characters)
   */
  async generateStandardDescription(
    content: string,
    standardId?: string,
    standardName?: string
  ): Promise<{ success: boolean; description: string; usage?: unknown }> {
    const response = await this.api.post('/ai/generate-description', {
      content,
      standardId,
      standardName
    });
    return response.data;
  }

  /**
   * Generate a SHORT description for homepage items using AI
   * @param description - The original description to condense
   * @param standardName - Optional name of the standard (for context)
   * @returns Generated short description (200-220 characters)
   */
  async generateShortDescription(
    description: string,
    standardName?: string
  ): Promise<{ success: boolean; description: string; charCount: number; usage?: unknown }> {
    const response = await this.api.post('/ai/generate-short-description', {
      description,
      standardName
    });
    return response.data;
  }

  // ============= QUIZ DISTRIBUTION METHODS =============

  /**
   * Get quiz distribution configuration
   * Controls how questions are selected by type and difficulty when users start quizzes
   */
  async getQuizDistributionConfig(): Promise<{
    success: boolean;
    config: QuizDistributionConfig;
  }> {
    const response = await this.api.get('/admin/quiz-distribution/config');
    return response.data;
  }

  /**
   * Update quiz distribution configuration
   */
  async updateQuizDistributionConfig(data: {
    typeDistribution?: Record<string, number>;
    difficultyDistribution?: Record<string, number>;
    distributionMode?: 'strict' | 'flexible';
  }): Promise<{
    success: boolean;
    config: QuizDistributionConfig;
    message: string;
  }> {
    const response = await this.api.put('/admin/quiz-distribution/config', data);
    return response.data;
  }

  /**
   * Reset quiz distribution configuration to defaults
   */
  async resetQuizDistributionConfig(): Promise<{
    success: boolean;
    config: QuizDistributionConfig;
    message: string;
  }> {
    const response = await this.api.post('/admin/quiz-distribution/reset');
    return response.data;
  }

  /**
   * Preview quiz distribution for a standard
   */
  async previewQuizDistribution(
    standardId: string,
    questionCount: number
  ): Promise<{
    success: boolean;
    preview: {
      available: {
        byType: Record<string, number>;
        byDifficulty: Record<string, number>;
        total: number;
      };
      projected: {
        requested: number;
        selected: number;
        byType: Record<string, number>;
        byDifficulty: Record<string, number>;
      };
      canMeetTargets: boolean;
    };
  }> {
    const response = await this.api.get('/admin/quiz-distribution/preview', {
      params: { standardId, count: questionCount }
    });
    return response.data;
  }

  /**
   * Get default quiz distribution values
   */
  async getQuizDistributionDefaults(): Promise<{
    success: boolean;
    defaults: {
      typeDistribution: Record<string, number>;
      difficultyDistribution: Record<string, number>;
      distributionMode: 'strict' | 'flexible';
    };
  }> {
    const response = await this.api.get('/admin/quiz-distribution/defaults');
    return response.data;
  }

  // ==================== Site Settings ====================

  /**
   * Get site settings (public - for Header component)
   */
  async getSiteSettings(): Promise<{
    id: string;
    logoUrl: string | null;
    showLogo: boolean;
    showDarkModeToggle: boolean;
    footerText: string | null;
    sampleQuizStandardId: string | null;
    sampleQuizQuestionIds: string[] | null;
    landingPageOgImage: string | null;
    enabledTours: {
      home: boolean;
      quizSetup: boolean;
      quiz: boolean;
      study: boolean;
      analytics: boolean;
    };
    createdAt: string;
    updatedAt: string;
  }> {
    const response = await this.api.get('/site-settings');
    return response.data;
  }

  /**
   * Update site settings (admin only)
   */
  async updateSiteSettings(settings: {
    logoUrl?: string | null;
    showLogo?: boolean;
    showDarkModeToggle?: boolean;
    footerText?: string | null;
    sampleQuizStandardId?: string | null;
    sampleQuizQuestionIds?: string[] | null;
    landingPageOgImage?: string | null;
    enabledTours?: {
      home: boolean;
      quizSetup: boolean;
      quiz: boolean;
      study: boolean;
      analytics: boolean;
    };
  }): Promise<{
    id: string;
    logoUrl: string | null;
    showLogo: boolean;
    showDarkModeToggle: boolean;
    footerText: string | null;
    sampleQuizStandardId: string | null;
    sampleQuizQuestionIds: string[] | null;
    landingPageOgImage: string | null;
    enabledTours: {
      home: boolean;
      quizSetup: boolean;
      quiz: boolean;
      study: boolean;
      analytics: boolean;
    };
    createdAt: string;
    updatedAt: string;
  }> {
    const response = await this.api.put('/site-settings', settings);
    return response.data;
  }

  /**
   * Get sample quiz for landing page (public)
   */
  async getSampleQuiz(): Promise<{
    standard: {
      id: string;
      code: string;
      title: string;
      nickname: string;
    } | null;
    questions: Array<{
      id: string;
      question: string;
      questionType: string;
      options: unknown;
      correctAnswer: unknown;
      explanation: string;
    }>;
  }> {
    const response = await this.api.get('/site-settings/sample-quiz');
    return response.data;
  }

  /**
   * Get questions for a standard (admin only - for question selection)
   */
  async getQuestionsForStandard(standardId: string): Promise<Array<{
    id: string;
    question: string;
    questionType: string;
    difficulty: string | null;
    sectionRefs: Array<{ level: number; title: string }> | null;
    options: unknown;
    correctAnswer: unknown;
    explanation: string;
  }>> {
    const response = await this.api.get(`/site-settings/questions/${standardId}`);
    return response.data;
  }

  // ==================== OG Image Settings ====================

  /**
   * OG Text field configuration interface
   */


  /**
   * Get OG image configuration (admin only)
   */
  async getOGConfig(): Promise<{
    ogTemplateUrl: string | null;
    ogTextConfig: {
      fields: {
        score: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right' };
        name: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right' };
        standard: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right' };
        correct: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right' };
        date: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right' };
        time: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right' };
        mode: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right' };
        difficulty: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right' };
        custom1: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right'; text?: string };
        custom2: { enabled: boolean; x: number; y: number; fontSize: number; color: string; align: 'left' | 'center' | 'right'; text?: string };
      };
    };
  }> {
    const response = await this.api.get('/site-settings/og-config');
    return response.data;
  }

  /**
   * Update OG image configuration (admin only)
   */
  async updateOGConfig(config: {
    ogTemplateUrl?: string | null;
    ogTextConfig?: unknown;
  }): Promise<{
    ogTemplateUrl: string | null;
    ogTextConfig: unknown;
  }> {
    const response = await this.api.put('/site-settings/og-config', config);
    return response.data;
  }

  /**
   * Upload OG template image (admin only)
   */
  async uploadOGTemplate(imageData: string, fileName: string): Promise<{
    success: boolean;
    ogTemplateUrl: string;
  }> {
    const response = await this.api.post('/site-settings/og-template', {
      imageData,
      fileName
    });
    return response.data;
  }

  /**
   * Generate OG image preview (admin only)
   */
  async generateOGPreview(ogTemplateUrl: string | null, ogTextConfig: unknown): Promise<{
    success: boolean;
    preview: string; // base64 data URL
  }> {
    const response = await this.api.post('/site-settings/og-preview', {
      ogTemplateUrl,
      ogTextConfig
    });
    return response.data;
  }

  // ============= SPACED REPETITION REVIEW METHODS =============

  /**
   * Add a wrong answer to the review queue
   */
  async addReviewItem(questionId: string, standardCode: string): Promise<{ success: boolean; data: ReviewItem }> {
    const response = await this.api.post('/review/items', { questionId, standardCode });
    return response.data;
  }

  /**
   * Get all review items for the user
   */
  async getReviewItems(standardCode?: string): Promise<{ success: boolean; data: ReviewItemWithQuestion[] }> {
    const response = await this.api.get('/review/items', {
      params: standardCode ? { standardCode } : undefined
    });
    return response.data;
  }

  /**
   * Get items due for review
   */
  async getDueReviewItems(limit?: number, standardCode?: string): Promise<{ success: boolean; data: ReviewItemWithQuestion[] }> {
    const response = await this.api.get('/review/items/due', {
      params: { limit, standardCode }
    });
    return response.data;
  }

  /**
   * Update a review item after answering (SM-2 algorithm)
   */
  async updateReviewItem(id: string, quality: 'again' | 'hard' | 'good' | 'easy'): Promise<{ success: boolean; data: ReviewItem }> {
    const response = await this.api.put(`/review/items/${id}`, { quality });
    return response.data;
  }

  /**
   * Delete a single review item
   */
  async deleteReviewItem(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/review/items/${id}`);
    return response.data;
  }

  /**
   * Clear all review items for the user
   */
  async clearAllReviewItems(): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete('/review/items');
    return response.data;
  }

  /**
   * Get review statistics
   */
  async getReviewStats(standardCode?: string): Promise<{ success: boolean; data: ReviewStats }> {
    const response = await this.api.get('/review/stats', {
      params: standardCode ? { standardCode } : undefined
    });
    return response.data;
  }

  /**
   * Get available standards with review item counts
   */
  async getReviewStandards(): Promise<{ success: boolean; data: ReviewStandardInfo[] }> {
    const response = await this.api.get('/review/standards');
    return response.data;
  }

  // ============= ANNOUNCEMENT METHODS =============

  /**
   * Get the active announcement for the current user
   */
  async getActiveAnnouncement(lastShownAt?: string): Promise<{ announcement: Announcement | null }> {
    const response = await this.api.get('/announcements/active', {
      params: lastShownAt ? { lastShownAt } : undefined
    });
    return response.data;
  }

  /**
   * Dismiss an announcement
   */
  async dismissAnnouncement(id: string): Promise<{ success: boolean; dismissalType: string }> {
    const response = await this.api.post(`/announcements/${id}/dismiss`);
    return response.data;
  }

  /**
   * List all announcements (admin)
   */
  async listAnnouncements(params?: {
    page?: number;
    limit?: number;
    status?: 'active' | 'scheduled' | 'expired' | 'inactive';
  }): Promise<AnnouncementListResponse> {
    const response = await this.api.get('/announcements/admin/list', { params });
    return response.data;
  }

  /**
   * Get a single announcement (admin)
   */
  async getAnnouncement(id: string): Promise<Announcement> {
    const response = await this.api.get(`/announcements/admin/${id}`);
    return response.data;
  }

  /**
   * Create an announcement (admin)
   */
  async createAnnouncement(data: CreateAnnouncementData): Promise<{ success: boolean; announcement: Announcement }> {
    const response = await this.api.post('/announcements/admin', data);
    return response.data;
  }

  /**
   * Update an announcement (admin)
   */
  async updateAnnouncement(id: string, data: Partial<CreateAnnouncementData>): Promise<{ success: boolean; announcement: Announcement }> {
    const response = await this.api.patch(`/announcements/admin/${id}`, data);
    return response.data;
  }

  /**
   * Delete an announcement (admin)
   */
  async deleteAnnouncement(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/announcements/admin/${id}`);
    return response.data;
  }

  /**
   * Toggle announcement active status (admin)
   */
  async toggleAnnouncementActive(id: string): Promise<{ success: boolean; isActive: boolean }> {
    const response = await this.api.patch(`/announcements/admin/${id}/toggle-active`);
    return response.data;
  }

}

// Export singleton instance
const apiService = new ApiService();
export default apiService;